import { debounce } from "./utilities-DtVOIXIY.js";
import "./waapi-patch-BCt4pEht.js";
import "./cookie-banner-layout-DZP7Q0uT.js";
import "./section-anchor-scroll-Bvm-mXVl.js";
import "./mobile-anim-gate-Btn1wVuD.js";
const MARQUEE_EASE = 0.06;
const MARQUEE_SETTLE = 0.5;
const MARQUEE_SPRING_STIFFNESS = 35;
const MARQUEE_SPRING_DAMPING = 9;
const MARQUEE_SPRING_RATE_SETTLE = 5;
const MARQUEE_SPRING_MAX_STEP = 1 / 120;
const MARQUEE_MAX_CLONES = 30;
const MARQUEE_ANIMATION_NAME = "embla-marquee-slide";
const MARQUEE_FLING_VIEWPORTS_PER_SECOND = 4;
const MARQUEE_MAX_FLING_SPEED = 3e3;
class EmblaMarqueeController {
  constructor({
    root,
    viewport,
    container,
    prefersReducedMotion = () => false,
    allowHoverPause = () => true,
    sanitizeClone = () => {
    },
    deferInit = false,
    manageFocus = true,
    nativeCursor = false
  }) {
    this.root = root;
    this.viewport = viewport;
    this.container = container;
    this.prefersReducedMotion = prefersReducedMotion;
    this.allowHoverPause = allowHoverPause;
    this.sanitizeClone = sanitizeClone;
    this.deferInit = deferInit;
    this.manageFocus = manageFocus;
    this.nativeCursor = nativeCursor;
    this.isActive = false;
    this.isInitializing = false;
    this._setupComplete = false;
    this._waitingForSize = false;
    this._restoreCssAfterResize = false;
    this.position = 0;
    this._rafId = null;
    this._initRafId = null;
    this._lastTime = 0;
    this._velocity = 0;
    this._targetVelocity = 0;
    this._springing = false;
    this._springVelocity = 0;
    this._baseSpeed = 0;
    this._direction = -1;
    this._groupWidth = 0;
    this._viewportWidth = 0;
    this._cssIdle = false;
    this._animationPending = false;
    this._dragging = false;
    this._dragLastX = 0;
    this._dragLastTime = 0;
    this._dragVelocity = 0;
    this._hoverPause = false;
    this._pausedBeforeSetup = false;
    this._pendingScrollIndex = null;
    this._intersectionObserver = null;
    this._resizeObserver = null;
    this._resizeHandler = null;
    this._onPointerEnter = null;
    this._onPointerLeave = null;
    this._onPointerDown = null;
    this._onPointerMove = null;
    this._onPointerUp = null;
    this._onFocusIn = null;
    this._onFocusOut = null;
    this._onScroll = null;
  }
  init(config = {}) {
    if (!this.root || !this.viewport || !this.container) return false;
    this.isActive = true;
    this._baseSpeed = (Number(config.speed) || 1) * 60;
    this._direction = config.direction === "backward" ? 1 : -1;
    this._hoverPause = config.stopOnMouseEnter === true;
    this._watchDrag = config.watchDrag !== false;
    if (this.deferInit) {
      this.isInitializing = true;
      this._initRafId = requestAnimationFrame(() => {
        this._initRafId = null;
        this.isInitializing = false;
        if (this.isActive) this._setup();
      });
    } else {
      this._setup();
    }
    return this.isActive;
  }
  pause() {
    if (!this.isActive) return;
    if (this.isInitializing) {
      this._pausedBeforeSetup = true;
      return;
    }
    this._stopSpring();
    this._enterRafMode();
    this._targetVelocity = 0;
  }
  resume() {
    if (!this.isActive || this.prefersReducedMotion()) return;
    this._stopSpring();
    this._pausedBeforeSetup = false;
    if (this.isInitializing) {
      this._pendingScrollIndex = null;
      return;
    }
    if (this._shouldPauseForInteraction()) {
      this._targetVelocity = 0;
      if (this._cssIdle) {
        this.container.style.animationPlayState = "paused";
      }
      return;
    }
    this._targetVelocity = this._baseSpeed * this._direction;
    if (this._cssIdle) {
      if (this.container.style.animationPlayState !== "running") {
        this.container.style.animationPlayState = "running";
      }
      return;
    }
    this._startRaf();
  }
  scrollTo(index) {
    if (!this.isActive || !this.container) return;
    if (this.isInitializing) {
      this._pendingScrollIndex = index;
      this._pausedBeforeSetup = true;
      return;
    }
    const slides = Array.from(
      this.container.querySelectorAll(
        ":scope > .embla__slide:not([data-cloned])"
      )
    );
    if (index < 0 || index >= slides.length) return;
    const gap = parseFloat(getComputedStyle(this.container).columnGap) || 0;
    const widths = slides.slice(0, index).map((slide) => slide.getBoundingClientRect().width);
    const targetPosition = -widths.reduce(
      (total, width) => total + width + gap,
      0
    );
    this.pause();
    this.position = targetPosition;
    this.container.style.animation = "none";
    this.container.style.transform = `translate3d(${this.position}px, 0, 0)`;
  }
  destroy() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m;
    if (this._initRafId !== null) cancelAnimationFrame(this._initRafId);
    this._initRafId = null;
    this.isInitializing = false;
    this._setupComplete = false;
    this._waitingForSize = false;
    this._restoreCssAfterResize = false;
    this._stopRaf();
    (_b = (_a = this._resizeHandler) == null ? void 0 : _a.cancel) == null ? void 0 : _b.call(_a);
    (_c = this._intersectionObserver) == null ? void 0 : _c.disconnect();
    (_d = this._resizeObserver) == null ? void 0 : _d.disconnect();
    if (this._onPointerEnter) {
      (_e = this.viewport) == null ? void 0 : _e.removeEventListener("pointerenter", this._onPointerEnter);
      (_f = this.viewport) == null ? void 0 : _f.removeEventListener("pointerleave", this._onPointerLeave);
    }
    if (this._onPointerDown) {
      (_g = this.viewport) == null ? void 0 : _g.removeEventListener("pointerdown", this._onPointerDown);
      (_h = this.viewport) == null ? void 0 : _h.removeEventListener("pointermove", this._onPointerMove);
      (_i = this.viewport) == null ? void 0 : _i.removeEventListener("pointerup", this._onPointerUp);
      (_j = this.viewport) == null ? void 0 : _j.removeEventListener("pointercancel", this._onPointerUp);
    }
    if (this._onFocusIn) {
      (_k = this.viewport) == null ? void 0 : _k.removeEventListener("focusin", this._onFocusIn);
      (_l = this.viewport) == null ? void 0 : _l.removeEventListener("focusout", this._onFocusOut);
      (_m = this.viewport) == null ? void 0 : _m.removeEventListener("scroll", this._onScroll);
    }
    if (this.viewport) {
      this.viewport.classList.remove("is-draggable", "is-dragging");
      this.viewport.style.touchAction = "";
      if (this.nativeCursor) this.viewport.style.cursor = "";
    }
    if (this.container) {
      this.container.querySelectorAll("[data-cloned]").forEach((clone) => clone.remove());
      this.container.style.animation = "";
      this.container.style.animationPlayState = "";
      this.container.style.transform = "";
      this.container.style.position = "";
      this.container.style.willChange = "";
      this.container.style.removeProperty("--_marquee-gw");
    }
    this.isActive = false;
    this.position = 0;
    this._velocity = 0;
    this._targetVelocity = 0;
    this._springing = false;
    this._springVelocity = 0;
    this._groupWidth = 0;
    this._cssIdle = false;
    this._animationPending = false;
    this._dragging = false;
    this._pausedBeforeSetup = false;
    this._pendingScrollIndex = null;
    this._intersectionObserver = null;
    this._resizeObserver = null;
    this._resizeHandler = null;
    this._onPointerEnter = this._onPointerLeave = null;
    this._onPointerDown = this._onPointerMove = this._onPointerUp = null;
    this._onFocusIn = this._onFocusOut = this._onScroll = null;
  }
  _setup() {
    this._measure();
    this._setupResize();
    if (this._groupWidth <= 0) {
      this.isInitializing = true;
      this._waitingForSize = true;
      return false;
    }
    this._completeSetup();
    return true;
  }
  _completeSetup() {
    if (!this.isActive || this._setupComplete || this._groupWidth <= 0) return;
    this.isInitializing = false;
    this._waitingForSize = false;
    this._setupComplete = true;
    const shouldStartPaused = this._pausedBeforeSetup || this._shouldPauseForInteraction();
    const initialVelocity = shouldStartPaused ? 0 : this._baseSpeed * this._direction;
    this._velocity = this._targetVelocity = initialVelocity;
    this.position = 0;
    this._setupVisibility();
    if (this._hoverPause) this._setupHoverPause();
    if (this._watchDrag) this._setupDrag();
    if (this.manageFocus) this._setupFocusManagement();
    if (shouldStartPaused) {
      this.container.style.animation = "none";
      this.container.style.transform = "translate3d(0, 0, 0)";
    } else {
      this._enterCssMode();
    }
    if (this._pendingScrollIndex !== null) {
      const index = this._pendingScrollIndex;
      this._pendingScrollIndex = null;
      this.scrollTo(index);
    }
  }
  _setupVisibility() {
    this._intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (this._cssIdle) {
              this._syncCss();
              this.container.style.animationPlayState = "running";
            } else {
              this._startRaf();
            }
          } else {
            this._stopRaf();
            if (this._cssIdle)
              this.container.style.animationPlayState = "paused";
          }
        });
      },
      { rootMargin: "100px" }
    );
    this._intersectionObserver.observe(this.root);
  }
  _shouldPauseForInteraction() {
    var _a, _b, _c, _d;
    const hoverPaused = this._hoverPause && this.allowHoverPause() && ((_b = (_a = this.viewport) == null ? void 0 : _a.matches) == null ? void 0 : _b.call(_a, ":hover"));
    const focusPaused = this.manageFocus && ((_d = (_c = this.viewport) == null ? void 0 : _c.contains) == null ? void 0 : _d.call(_c, document.activeElement));
    return hoverPaused || focusPaused;
  }
  _setupHoverPause() {
    this._onPointerEnter = () => {
      if (this.prefersReducedMotion() || !this.allowHoverPause()) return;
      if (this._cssIdle) {
        this.container.style.animationPlayState = "paused";
      } else {
        this._targetVelocity = 0;
      }
    };
    this._onPointerLeave = () => {
      if (this.prefersReducedMotion() || this._dragging) return;
      if (this._cssIdle) {
        this.container.style.animationPlayState = "running";
      } else {
        this._targetVelocity = this._baseSpeed * this._direction;
        this._startRaf();
      }
    };
    this.viewport.addEventListener("pointerenter", this._onPointerEnter);
    this.viewport.addEventListener("pointerleave", this._onPointerLeave);
  }
  _setupDrag() {
    this.viewport.classList.add("is-draggable");
    this._onPointerDown = (event) => {
      if (event.button !== 0) return;
      this._enterRafMode();
      this._startRaf();
      this._dragging = true;
      this._dragLastX = event.clientX;
      this._dragLastTime = event.timeStamp;
      this._dragVelocity = 0;
      this._stopSpring();
      this._velocity = this._targetVelocity = 0;
      this.viewport.setPointerCapture(event.pointerId);
      this.viewport.classList.add("is-dragging");
      if (this.nativeCursor) this.viewport.style.cursor = "grabbing";
    };
    this._onPointerMove = (event) => {
      var _a;
      if (!this._dragging) return;
      event.preventDefault();
      const coalesced = (_a = event.getCoalescedEvents) == null ? void 0 : _a.call(event);
      const samples = (coalesced == null ? void 0 : coalesced.length) ? coalesced : [event];
      samples.forEach((sample) => {
        const delta = sample.clientX - this._dragLastX;
        const elapsed = sample.timeStamp - this._dragLastTime;
        if (elapsed > 0) {
          const instantVelocity = delta / elapsed * 1e3;
          const blend = 1 - Math.exp(-elapsed / 32);
          this._dragVelocity += (instantVelocity - this._dragVelocity) * blend;
        }
        this.position += delta;
        this._dragLastX = sample.clientX;
        this._dragLastTime = sample.timeStamp;
      });
    };
    this._onPointerUp = (event) => {
      if (!this._dragging) return;
      if (event.type !== "pointercancel" && Number.isFinite(event.clientX)) {
        this.position += event.clientX - this._dragLastX;
      }
      this._dragging = false;
      if (this.viewport.hasPointerCapture(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }
      this.viewport.classList.remove("is-dragging");
      if (this.nativeCursor) this.viewport.style.cursor = "grab";
      const releaseIdle = Math.max(0, event.timeStamp - this._dragLastTime);
      const releaseVelocity = event.type === "pointercancel" ? 0 : this._dragVelocity * Math.exp(-releaseIdle / 80);
      const cap = Math.min(
        MARQUEE_MAX_FLING_SPEED,
        Math.max(
          this._baseSpeed * 6,
          this._viewportWidth * MARQUEE_FLING_VIEWPORTS_PER_SECOND
        )
      );
      const reduceMotion = this.prefersReducedMotion();
      this._velocity = reduceMotion ? 0 : Math.max(-cap, Math.min(cap, releaseVelocity));
      if (!reduceMotion) {
        const remainPaused = this._hoverPause && this.allowHoverPause() && this.viewport.matches(":hover");
        this._targetVelocity = remainPaused ? 0 : this._baseSpeed * this._direction;
      }
      this._springing = !reduceMotion && event.type !== "pointercancel" && Math.abs(this._targetVelocity - this._velocity) >= MARQUEE_SETTLE;
      this._springVelocity = 0;
      this._startRaf();
    };
    this.viewport.addEventListener("pointerdown", this._onPointerDown);
    this.viewport.addEventListener("pointermove", this._onPointerMove);
    this.viewport.addEventListener("pointerup", this._onPointerUp);
    this.viewport.addEventListener("pointercancel", this._onPointerUp);
    this.viewport.style.touchAction = "pan-y pinch-zoom";
    if (this.nativeCursor) this.viewport.style.cursor = "grab";
  }
  _setupFocusManagement() {
    this._onFocusIn = (event) => {
      if (this.prefersReducedMotion()) return;
      const slide = event.target.closest(".embla__slide");
      const viewportRect = slide ? this.viewport.getBoundingClientRect() : null;
      const slideRect = slide ? slide.getBoundingClientRect() : null;
      let shift = 0;
      if (viewportRect && slideRect) {
        if (slideRect.left < viewportRect.left) {
          shift = viewportRect.left - slideRect.left;
        } else if (slideRect.right > viewportRect.right) {
          shift = viewportRect.right - slideRect.right;
        }
      }
      this._targetVelocity = 0;
      this._stopSpring();
      if (shift !== 0) {
        this._enterRafMode();
        this.position = Math.min(0, this.position + shift);
        this.container.style.transform = `translate3d(${this.position}px, 0, 0)`;
      } else if (this._cssIdle) {
        this.container.style.animationPlayState = "paused";
      }
    };
    this._onFocusOut = (event) => {
      if (this.prefersReducedMotion() || this._dragging) return;
      if (this.viewport.contains(event.relatedTarget)) return;
      if (this._hoverPause && this.allowHoverPause() && this.viewport.matches(":hover"))
        return;
      this.resume();
    };
    this._onScroll = () => {
      const scrollLeft = this.viewport.scrollLeft;
      if (scrollLeft <= 0) return;
      this._enterRafMode();
      this.position -= scrollLeft;
      this.viewport.scrollLeft = 0;
      this.container.style.transform = `translate3d(${this.position}px, 0, 0)`;
    };
    this.viewport.addEventListener("focusin", this._onFocusIn);
    this.viewport.addEventListener("focusout", this._onFocusOut);
    this.viewport.addEventListener("scroll", this._onScroll);
  }
  _setupResize() {
    if (this._resizeObserver) return;
    let lastWidth = this._viewportWidth;
    this._resizeHandler = debounce((entries) => {
      if (!this.isActive) return;
      const viewportEntry = entries.find(
        (entry) => entry.target === this.viewport
      );
      const width = (viewportEntry == null ? void 0 : viewportEntry.contentRect.width) ?? lastWidth;
      const widthChanged = Math.abs(width - lastWidth) >= 0.5;
      lastWidth = width;
      if (!this._setupComplete) {
        this._measure();
        if (this._groupWidth > 0) this._completeSetup();
        return;
      }
      if (!widthChanged && !this._waitingForSize) return;
      const wasCssIdle = this._cssIdle;
      if (wasCssIdle) this._enterRafMode();
      this._measure();
      if (this._groupWidth <= 0) {
        this.isInitializing = true;
        this._waitingForSize = true;
        this._restoreCssAfterResize || (this._restoreCssAfterResize = wasCssIdle);
        this._stopRaf();
        return;
      }
      const restoreCss = wasCssIdle || this._restoreCssAfterResize;
      const shouldStayPaused = this._pausedBeforeSetup || this._shouldPauseForInteraction();
      this.isInitializing = false;
      this._waitingForSize = false;
      this._restoreCssAfterResize = false;
      if (restoreCss && !shouldStayPaused) {
        this.position = 0;
        this._velocity = this._targetVelocity = this._baseSpeed * this._direction;
        this._animationPending = false;
        this._enterCssMode();
      } else if (!shouldStayPaused && (this._targetVelocity !== 0 || this._velocity !== 0)) {
        this._startRaf();
      } else {
        this.container.style.animation = "none";
        this.container.style.transform = `translate3d(${this.position}px, 0, 0)`;
      }
      if (this._pendingScrollIndex !== null) {
        const index = this._pendingScrollIndex;
        this._pendingScrollIndex = null;
        this.scrollTo(index);
      }
    }, 250);
    this._resizeObserver = new ResizeObserver(this._resizeHandler);
    this._resizeObserver.observe(this.viewport);
    this._resizeObserver.observe(this.container);
  }
  _measure() {
    if (!this.container || !this.viewport) return;
    const slides = Array.from(
      this.container.querySelectorAll(
        ":scope > .embla__slide:not([data-cloned])"
      )
    );
    if (!slides.length) {
      this._viewportWidth = this.viewport.getBoundingClientRect().width;
      this._groupWidth = 0;
      return;
    }
    const gap = parseFloat(getComputedStyle(this.container).columnGap) || 0;
    const containerRect = this.container.getBoundingClientRect();
    const slideRects = slides.map((slide) => slide.getBoundingClientRect());
    const slideWidths = slideRects.map((rect) => rect.width);
    const slideOffsets = slideRects.map((rect) => ({
      top: rect.top - containerRect.top,
      height: rect.height
    }));
    const viewportWidth = this.viewport.getBoundingClientRect().width;
    const groupWidth = slideWidths.reduce(
      (total, width) => total + width + gap,
      0
    );
    this._viewportWidth = viewportWidth;
    this._groupWidth = groupWidth;
    if (groupWidth <= 0) return;
    const requiredWidth = viewportWidth + groupWidth;
    let currentWidth = groupWidth;
    const cloneIndexes = [];
    const cloneOffsets = [];
    while (currentWidth < requiredWidth && cloneIndexes.length < slides.length * MARQUEE_MAX_CLONES) {
      for (let index = 0; index < slides.length; index += 1) {
        if (currentWidth >= requiredWidth) break;
        cloneIndexes.push(index);
        cloneOffsets.push(currentWidth);
        currentWidth += slideWidths[index] + gap;
      }
    }
    this.container.querySelectorAll("[data-cloned]").forEach((clone) => clone.remove());
    this.container.style.position = "relative";
    this.container.style.willChange = "transform";
    if (!cloneIndexes.length) return;
    const fragment = document.createDocumentFragment();
    cloneIndexes.forEach((slideIndex, cloneIndex) => {
      const source = slides[slideIndex];
      const clone = source.cloneNode(true);
      clone.dataset.cloned = "";
      clone.dataset.marqueeSourceIndex = String(slideIndex);
      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("inert", "");
      clone.style.position = "absolute";
      clone.style.top = `${slideOffsets[slideIndex].top}px`;
      clone.style.left = `${cloneOffsets[cloneIndex]}px`;
      clone.style.height = `${slideOffsets[slideIndex].height}px`;
      clone.querySelectorAll("img").forEach((image) => {
        var _a;
        image.setAttribute("loaded", "");
        (_a = image.parentElement) == null ? void 0 : _a.removeAttribute("data-lazy-loading");
      });
      this.sanitizeClone(clone, source, slideIndex);
      fragment.appendChild(clone);
    });
    this.container.appendChild(fragment);
  }
  _enterCssMode() {
    if (this._cssIdle || this._groupWidth <= 0) return;
    this._stopSpring();
    this._cssIdle = true;
    this._stopRaf();
    this._syncCss();
  }
  _syncCss() {
    if (!this._cssIdle || !this.container || this._groupWidth <= 0) return;
    if (this._baseSpeed <= 0) {
      this.container.style.animation = "none";
      return;
    }
    const duration = this._groupWidth / this._baseSpeed;
    const direction = this._direction === -1 ? "normal" : "reverse";
    const normalizedPosition = (this.position % this._groupWidth + this._groupWidth) % this._groupWidth;
    const fraction = this._direction === -1 ? (this._groupWidth - normalizedPosition) / this._groupWidth : normalizedPosition / this._groupWidth;
    const delay = -(fraction * duration);
    this.container.style.setProperty("--_marquee-gw", this._groupWidth);
    this.container.style.animation = `${MARQUEE_ANIMATION_NAME} ${duration}s linear ${delay}s infinite ${direction}`;
    this.container.style.animationPlayState = "running";
  }
  _enterRafMode() {
    var _a;
    if (!this._cssIdle) return;
    const cssAnimation = this.container.getAnimations().find((animation) => animation.animationName === MARQUEE_ANIMATION_NAME);
    const progress = (_a = cssAnimation == null ? void 0 : cssAnimation.effect) == null ? void 0 : _a.getComputedTiming().progress;
    if (Number.isFinite(progress)) {
      cssAnimation.pause();
      this.position = -progress * this._groupWidth;
    } else {
      this.container.style.animationPlayState = "paused";
      this.position = new DOMMatrix(
        getComputedStyle(this.container).transform
      ).m41;
    }
    this._cssIdle = false;
    this._animationPending = true;
    this._startRaf();
  }
  _startRaf() {
    if (this._rafId !== null) return;
    this._lastTime = performance.now();
    this._rafId = requestAnimationFrame((time) => this._tick(time));
  }
  _stopRaf() {
    if (this._rafId === null) return;
    cancelAnimationFrame(this._rafId);
    this._rafId = null;
  }
  _stopSpring() {
    this._springing = false;
    this._springVelocity = 0;
  }
  _tick(time) {
    this._rafId = null;
    if (!this.isActive || this._cssIdle) return;
    const elapsed = Math.min((time - this._lastTime) / 1e3, 0.06);
    this._lastTime = time;
    if (!this.container || this._groupWidth <= 0) return;
    if (this._animationPending) {
      this._animationPending = false;
      this.container.style.animation = "none";
      this.container.style.transform = `translate3d(${this.position}px, 0, 0)`;
      this._rafId = requestAnimationFrame((nextTime) => this._tick(nextTime));
      return;
    }
    if (!this._dragging) {
      if (this._springing) {
        const steps = Math.max(1, Math.ceil(elapsed / MARQUEE_SPRING_MAX_STEP));
        const springStep = elapsed / steps;
        for (let index = 0; index < steps; index += 1) {
          const velocityDelta = this._targetVelocity - this._velocity;
          this._springVelocity += (velocityDelta * MARQUEE_SPRING_STIFFNESS - this._springVelocity * MARQUEE_SPRING_DAMPING) * springStep;
          this._velocity += this._springVelocity * springStep;
          this.position += this._velocity * springStep;
        }
        if (Math.abs(this._targetVelocity - this._velocity) < MARQUEE_SETTLE && Math.abs(this._springVelocity) < MARQUEE_SPRING_RATE_SETTLE) {
          this._velocity = this._targetVelocity;
          this._stopSpring();
        }
      } else {
        const velocityDelta = this._targetVelocity - this._velocity;
        if (Math.abs(velocityDelta) < MARQUEE_SETTLE) {
          this._velocity = this._targetVelocity;
        } else {
          const frameEase = 1 - Math.pow(1 - MARQUEE_EASE, elapsed * 60);
          this._velocity += velocityDelta * frameEase;
        }
        this.position += this._velocity * elapsed;
      }
    }
    if (!this.viewport.contains(document.activeElement)) {
      this.position = (this.position % this._groupWidth + this._groupWidth) % this._groupWidth;
      if (this.position > 0) this.position -= this._groupWidth;
    } else if (this.position > 0) {
      this.position = 0;
      this._velocity = 0;
    } else if (this.position < -this._groupWidth) {
      this.position = -this._groupWidth;
      this._velocity = 0;
    }
    this.container.style.transform = `translate3d(${this.position}px, 0, 0)`;
    if (!this._dragging && this._velocity === this._targetVelocity) {
      if (this._targetVelocity !== 0) this._enterCssMode();
      return;
    }
    this._rafId = requestAnimationFrame((nextTime) => this._tick(nextTime));
  }
}
export {
  EmblaMarqueeController
};
